import { memo, type ReactNode } from "react";
import { type GoogleMarker } from "~/domains/salesPartner/types/SalesPartnerTypes";
import GmapsCardRightUnregistered from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetailsCardRightUnregistered";
import GmapsCardRightRegistered from "~/features/salesPartner/components/CompanyDetailsCard/SPCompanyDetailsCardRightRegistered";
import { userIsLoggedIn, userIsRegistered } from "~/domains/user/utils/roles";
import classNames from "classnames";
import { Competences, SPCompanyDetails } from "./SPCompanyDetails";
import { useTranslation } from "react-i18next";
import { Box, Button, IconButton, styled } from "~/common/mui";
import { useIsMinDesktopSize } from "~/common/hooks/useMediaQuery";
import { useGuestCheckoutDialog } from "~/features/cart/B2CCart/components/GuestCheckoutDialog/useGuestCheckoutDialog";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import type { SalesPartnerSearchFormValues } from "~/app/analytics/ga/salesPartner";

interface Props {
  className?: string;
  selectedMarker: GoogleMarker;
  activeTab: string;
  shareLinearConfigurationData?: string[];
  action?: ReactNode;
  openRegisterSnackbar?: () => void;
  isCheckout?: boolean;
  searchValues: SalesPartnerSearchFormValues;
  onClose?: () => void;
}

export const SPCompanyDetailsCard = memo((props: Props) => {
  const { selectedMarker, activeTab, className, isCheckout, onClose } = props;

  const { t } = useTranslation();

  const isDesktop = useIsMinDesktopSize();

  const loggedIn = userIsLoggedIn();
  const registeredUser = userIsRegistered();

  const [handleB2CCheckout, guestCheckoutDialog] = useGuestCheckoutDialog();

  return (
    <StyledDetailsCard
      className={classNames("scha__sp__details-card", className)}
      $isCheckout={isCheckout}
      $isMobile={!isDesktop}
    >
      {onClose && (
        <StyledIconButton variant="contained" color={"secondary"} size={"tiny"} onClick={() => onClose()}>
          <CloseRoundedIcon />
        </StyledIconButton>
      )}
      <DetailsCardContent $withGap={isCheckout}>
        {guestCheckoutDialog}
        <DetailsLeft
          $framed={!isCheckout}
          openRegisterSnackbar={props.openRegisterSnackbar}
          action={props.action}
          company={selectedMarker}
          activeTab={activeTab}
          isCheckout={isCheckout}
          searchValues={props.searchValues}
        />

        {!isCheckout ? (
          <DetailsRight>
            {!loggedIn && <GmapsCardRightUnregistered company={selectedMarker} searchValues={props.searchValues} />}
            {registeredUser && (
              <GmapsCardRightRegistered
                shareLinearConfigurationData={props.shareLinearConfigurationData}
                companyDetails={selectedMarker.address}
                company={selectedMarker}
                searchValues={props.searchValues}
              />
            )}
          </DetailsRight>
        ) : (
          <LoggedInRight>
            {Competences(selectedMarker.certifiedCompetences, selectedMarker.productCompetences)}
          </LoggedInRight>
        )}
      </DetailsCardContent>
      {isDesktop && isCheckout && (
        <ConfirmActions>
          <Button onClick={() => handleB2CCheckout(selectedMarker.subsidiaryId)}>
            {t("partnerFinder.search.confirm")}
          </Button>
        </ConfirmActions>
      )}
    </StyledDetailsCard>
  );
});

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  right: theme.spacing(2),
  top: theme.spacing(2),
}));

const StyledDetailsCard = styled(Box, {
  shouldForwardProp: (prop: string) => !prop.startsWith("$"),
})<{ $isCheckout?: boolean; $isMobile?: boolean }>(({ theme, $isCheckout, $isMobile }) => ({
  padding: theme.spacing(3),
  background: theme.palette.common.white,
  position: "relative",
  margin: `${theme.spacing(3)} ${theme.spacing(3)} 0`,
  alignSelf: "flex-start",
  display: "flex",
  flexDirection: "column",
  borderRadius: theme.spacing(2),
  ...($isCheckout && { justifyContent: "space-between" }),
  ...($isMobile && {
    "&::after": {
      content: "none",
    },
  }),
  [theme.breakpoints.up("tablet")]: {
    padding: 0,
  },
  [theme.breakpoints.up("desktop-s")]: {
    padding: theme.spacing(3),
  },
}));

const DetailsCardContent = styled(Box, {
  shouldForwardProp: (prop: string) => prop !== "$withGap",
})<{ $withGap?: boolean }>(({ theme, $withGap }) => ({
  display: "flex",
  ...($withGap && {
    gap: theme.spacing(5),
  }),
}));

const DetailsLeft = styled(SPCompanyDetails, {
  shouldForwardProp: (prop: string) => prop !== "$framed",
})<{ $framed?: boolean }>(({ theme, $framed }) => ({
  ...($framed && {
    flex: 0.65,
    borderRight: `1px solid ${theme.palette.divider}`,
    display: "flex",
    paddingRight: theme.spacing(3),
  }),
}));

const DetailsRight = styled("div")(({ theme }) => ({
  flex: 0.35,
  paddingLeft: theme.spacing(3),
}));

const LoggedInRight = styled("div")({
  display: "flex",
  flexDirection: "column",
  flex: 1,
});

const ConfirmActions = styled("div")(({ theme }) => ({
  alignSelf: "flex-end",
  marginTop: theme.spacing(2),
}));
